import { useEffect, useState } from "react";
import {
  adminListIntensiveOffers,
  adminCreateIntensiveOffer,
  adminResendIntensiveOffer,
  adminCancelIntensiveOffer,
} from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import { Copy, Send, X, AlertTriangle, CheckCircle2 } from "lucide-react";
import { extractErrorMessage } from "../../lib/errors";
import IntensiveSlotPicker from "../../components/IntensiveSlotPicker";

function StatusBadge({ status }) {
  const styles = {
    pending: "bg-[#FEF3C7] text-[#92400E]",
    paid: "bg-[#DCFCE7] text-[#15803D]",
    expired: "bg-[#F3F4F6] text-[#6B7280]",
    cancelled: "bg-[#FEE2E2] text-[#B91C1C]",
  };
  return (
    <span className={`inline-flex items-center text-[10px] uppercase tracking-[0.15em] font-semibold px-2 py-0.5 rounded-full ${styles[status] || styles.pending}`}>
      {status}
    </span>
  );
}

export default function AdminIntensiveOffers() {
  const [offers, setOffers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    course_label: "Beginner Intensive — 35 hours",
    transmission: "manual",
    amount: "",
    notes: "",
    pickup_postcode: "",
    slots: [],
  });

  const load = () =>
    adminListIntensiveOffers().then(setOffers).catch(() => toast.error("Could not load offers"));

  useEffect(() => { load(); }, []);

  const updateForm = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const setSlots = (slots) => setForm(prev => ({ ...prev, slots }));
  const removeSlot = (i) =>
    setForm(prev => ({ ...prev, slots: prev.slots.filter((_, idx) => idx !== i) }));

  const resetForm = () => {
    setForm({
      customer_name: "", customer_email: "", customer_phone: "",
      course_label: "Beginner Intensive — 35 hours",
      transmission: "manual", amount: "", notes: "", pickup_postcode: "",
      slots: [],
    });
  };

  const createOffer = async (ev) => {
    ev.preventDefault();
    if (!form.customer_name || !form.customer_email || !form.customer_phone || !form.course_label) {
      return toast.error("Fill customer details and course label");
    }
    if (!form.amount || Number(form.amount) <= 0) {
      return toast.error("Enter a positive amount");
    }
    if (!form.slots.length) {
      return toast.error("Pick at least one slot on the calendar");
    }
    if (!form.slots.every(s => s.date && s.time)) {
      return toast.error("All slots need a date and time");
    }
    try {
      setCreating(true);
      const res = await adminCreateIntensiveOffer({
        ...form,
        amount: Number(form.amount),
      });
      await navigator.clipboard?.writeText(res.offer_url).catch(() => {});
      toast.success(
        `Offer created and emailed to ${form.customer_email}. Link copied to clipboard.`,
        { duration: 7000 }
      );
      resetForm();
      setShowForm(false);
      await load();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not create offer"));
    } finally {
      setCreating(false);
    }
  };

  const resendOffer = async (offer_id) => {
    try {
      const res = await adminResendIntensiveOffer(offer_id);
      if (res.ok === false && res.conflicts) {
        toast.error(
          `${res.conflicts.length} slot(s) now unavailable. Cancel the offer and recreate with new slots.`,
          { duration: 10000 }
        );
      } else {
        toast.success("Offer email resent.");
      }
      await load();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Resend failed"));
    }
  };

  const cancelOffer = async (offer) => {
    const isPaid = offer.status === "paid";
    const msg = isPaid
      ? `Cancel this PAID intensive course for ${offer.customer_name}?\n\nThis will:\n• Remove all ${offer.slots.length} lesson${offer.slots.length === 1 ? "" : "s"} from the calendar\n• Email the customer\n• Mark the offer as cancelled\n\nRefunds must be processed separately on the Orders page.`
      : `Cancel this offer for ${offer.customer_name} and release the ${offer.slots.length} slot${offer.slots.length === 1 ? "" : "s"}?`;
    if (!window.confirm(msg)) return;
    try {
      const res = await adminCancelIntensiveOffer(offer.offer_id);
      if (res.booking_cancelled) {
        toast.success("Offer cancelled, lessons removed from calendar & customer emailed. Refund via Orders.", { duration: 7000 });
      } else if (res.booking_deleted) {
        toast.success("Offer cancelled & pending checkout cleared.");
      } else {
        toast.success("Offer cancelled.");
      }
      await load();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Cancel failed"));
    }
  };

  const copyLink = (origin, offer_id) => {
    const url = `${origin}/intensive-offer/${offer_id}`;
    navigator.clipboard?.writeText(url).then(() => toast.success("Link copied"));
  };

  return (
    <div data-testid="admin-intensive-offers-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl tracking-tight font-medium" style={{ fontFamily: "Outfit, sans-serif" }}>Intensive course offers</h1>
          <p className="text-sm text-[#4B5563]">Build a tailored intensive course package and email a secure payment link to the customer. Slots lock for 48 hours.</p>
        </div>
        <Button
          onClick={() => setShowForm(s => !s)}
          className="bg-[#37B160] hover:bg-[#2D9450] text-white"
          data-testid="toggle-create-offer-btn"
        >
          {showForm ? "Close form" : "+ New offer"}
        </Button>
      </div>

      {showForm && (
        <form onSubmit={createOffer} className="mt-6 bg-white border border-black/5 rounded-2xl p-5 space-y-4" data-testid="create-offer-form">
          <h2 className="text-lg font-medium" style={{ fontFamily: "Outfit, sans-serif" }}>New offer</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Customer name</Label>
              <Input value={form.customer_name} onChange={e => updateForm("customer_name", e.target.value)} data-testid="offer-customer-name" required />
            </div>
            <div>
              <Label className="text-xs">Customer email</Label>
              <Input type="email" value={form.customer_email} onChange={e => updateForm("customer_email", e.target.value)} data-testid="offer-customer-email" required />
            </div>
            <div>
              <Label className="text-xs">Customer phone</Label>
              <Input value={form.customer_phone} onChange={e => updateForm("customer_phone", e.target.value)} data-testid="offer-customer-phone" required />
            </div>
            <div>
              <Label className="text-xs">Pickup postcode</Label>
              <Input value={form.pickup_postcode} onChange={e => updateForm("pickup_postcode", e.target.value.toUpperCase())} placeholder="SO14 1AB" data-testid="offer-postcode" />
            </div>
            <div>
              <Label className="text-xs">Course label</Label>
              <Input value={form.course_label} onChange={e => updateForm("course_label", e.target.value)} data-testid="offer-course-label" required />
            </div>
            <div>
              <Label className="text-xs">Transmission</Label>
              <select value={form.transmission} onChange={e => updateForm("transmission", e.target.value)} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="offer-transmission">
                <option value="manual">Manual</option>
                <option value="auto">Automatic</option>
              </select>
            </div>
            <div>
              <Label className="text-xs">Total amount (£)</Label>
              <Input type="number" min="0" step="0.01" value={form.amount} onChange={e => updateForm("amount", e.target.value)} data-testid="offer-amount" required />
            </div>
          </div>
          <div>
            <Label className="text-xs">Internal notes / what to tell the customer (optional)</Label>
            <Textarea rows={2} value={form.notes} onChange={e => updateForm("notes", e.target.value)} placeholder="e.g. Please arrive 10 minutes before each session." data-testid="offer-notes" />
          </div>

          <div className="pt-2">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs">Pick slots on the calendar ({form.slots.length} selected)</Label>
              {form.slots.length > 0 && (
                <Button type="button" variant="ghost" size="sm" onClick={() => setSlots([])}>
                  Clear all
                </Button>
              )}
            </div>

            <IntensiveSlotPicker
              selected={form.slots}
              onChange={setSlots}
              transmission={form.transmission}
            />

            {form.slots.length > 0 && (
              <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
                <div className="text-xs text-[#4B5563]" data-testid="offer-slots-tally">
                  Picked: <span className="font-semibold text-[#111827]">{form.slots.length} slot{form.slots.length === 1 ? "" : "s"}</span>
                  <span className="text-[#9CA3AF]"> · </span>
                  <span className="font-semibold text-[#37B160]">{form.slots.length * 2}h</span>
                </div>
              </div>
            )}

            {form.slots.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2" data-testid="offer-selected-slots">
                {form.slots.map((s, i) => (
                  <span
                    key={`${s.date}|${s.time}`}
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#37B160]/10 text-[#15803D] border border-[#37B160]/30 px-2.5 py-1 text-xs font-medium"
                    data-testid={`offer-slot-chip-${i}`}
                  >
                    {s.date} · {s.time}
                    <button
                      type="button"
                      onClick={() => removeSlot(i)}
                      className="hover:text-red-600"
                      aria-label="Remove slot"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => { resetForm(); setShowForm(false); }}>Cancel</Button>
            <Button type="submit" disabled={creating} className="bg-[#37B160] hover:bg-[#2D9450] text-white" data-testid="offer-submit">
              {creating ? "Creating…" : "Create & send offer"}
            </Button>
          </div>
        </form>
      )}

      <div className="mt-8 space-y-3">
        {offers.map(o => {
          const hasConflicts = (o.conflict_slots || []).length > 0;
          return (
            <div key={o.offer_id} className={`bg-white border rounded-2xl p-5 ${hasConflicts ? 'border-[#DC2626] bg-[#FEF2F2]' : 'border-black/5'}`} data-testid={`offer-card-${o.offer_id}`}>
              {hasConflicts && (
                <div className="mb-3 flex items-start gap-2 bg-[#FEE2E2] text-[#991B1B] p-3 rounded-lg text-sm" data-testid="offer-conflict-banner">
                  <AlertTriangle className="h-4 w-4 flex-none mt-0.5" />
                  <div>
                    <strong>{o.conflict_slots.length} slot(s) no longer available:</strong>{" "}
                    {o.conflict_slots.map(c => `${c.date} ${c.time}`).join(", ")}. Cancel and recreate with new slots.
                  </div>
                </div>
              )}
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{o.customer_name}</span>
                    <StatusBadge status={o.status} />
                    {o.status === "paid" && <CheckCircle2 className="h-4 w-4 text-[#15803D]" />}
                  </div>
                  <div className="text-xs text-[#4B5563] mt-1 truncate">{o.customer_email} · {o.customer_phone}</div>
                  <div className="text-sm font-medium text-[#0A0F00] mt-2">{o.course_label}</div>
                  <div className="text-xs text-[#4B5563] mt-1">
                    {o.transmission === "auto" ? "Automatic" : "Manual"} · £{o.amount?.toFixed(2)} · {o.slots.length} slots
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {o.status === "pending" && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => copyLink(window.location.origin, o.offer_id)} data-testid={`offer-copy-link-${o.offer_id}`}>
                        <Copy className="h-3 w-3 mr-1" /> Copy link
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => resendOffer(o.offer_id)} data-testid={`offer-resend-${o.offer_id}`}>
                        <Send className="h-3 w-3 mr-1" /> Resend
                      </Button>
                    </>
                  )}
                  {o.status === "expired" && (
                    <Button size="sm" variant="outline" onClick={() => resendOffer(o.offer_id)} data-testid={`offer-resend-${o.offer_id}`}>
                      <Send className="h-3 w-3 mr-1" /> Re-send & re-lock
                    </Button>
                  )}
                  {(o.status === "pending" || o.status === "expired") && (
                    <Button size="sm" variant="ghost" onClick={() => cancelOffer(o)} data-testid={`offer-cancel-${o.offer_id}`}>
                      <X className="h-3 w-3 mr-1" /> Cancel
                    </Button>
                  )}
                  {o.status === "paid" && (
                    <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => cancelOffer(o)} data-testid={`offer-cancel-${o.offer_id}`}>
                      <X className="h-3 w-3 mr-1" /> Cancel course
                    </Button>
                  )}
                </div>
              </div>
              <div className="mt-3 text-xs text-[#4B5563] space-y-1">
                <div>Slots: {o.slots.map(s => `${s.date} ${s.time}`).join(" · ")}</div>
                <div>
                  Created {new Date(o.created_at).toLocaleString("en-GB")}
                  {o.last_sent_at ? ` · last sent ${new Date(o.last_sent_at).toLocaleString("en-GB")}` : ""}
                  {o.status === "pending" ? ` · expires ${new Date(o.expires_at).toLocaleString("en-GB")}` : ""}
                </div>
              </div>
            </div>
          );
        })}
        {offers.length === 0 && <div className="text-[#9CA3AF]">No intensive offers yet. Click "+ New offer" to create one.</div>}
      </div>
    </div>
  );
}
